import { animate, eases } from "animejs"
import classNames from "classnames"
import React, { FC, useEffect, useRef, useState } from "react"

import OutlineButton from "@/components/OutlineButton"
import OutlinedText from "@/components/OutlinedText"
import { i18n } from "@/i18n"
import { getFontSize } from "@/utils/adaptFont"

import styles from "./index.module.scss"

// ==================== 字体缩放辅助函数 ====================
/** 将设计稿 px 值转换为考虑字体缩放后的值 */
const scalePx = (px: number): number => {
    const baseFontSize = 16
    const currentFontSize = getFontSize()
    return px * (currentFontSize / baseFontSize)
}

// ==================== 常量定义 ====================
/** 进度条进入动画时长（毫秒） */
const PROGRESS_ENTRY_DURATION = 500
/** 进度条进入动画初始Y偏移（像素）- 设计稿值，实际使用时需要缩放 */
const PROGRESS_ENTRY_INITIAL_Y_DESIGN = 50

/** icon旋转动画时长（毫秒） */
const ICON_ROTATION_DURATION = 600
/** icon旋转角度（度） */
const ICON_ROTATION_ANGLE = 360

/** 进度条渐隐动画时长（毫秒） */
const PROGRESS_EXIT_DURATION = 300
/** 进度条渐隐上移距离（像素）- 设计稿值，实际使用时需要缩放 */
const PROGRESS_EXIT_TRANSLATE_Y_DESIGN = 0
/** 进度条渐隐缩放比例 */
const PROGRESS_EXIT_SCALE = 0.95

/** 开始按钮弹入动画时长（毫秒） */
const BUTTON_ENTRY_DURATION = 500
/** 开始按钮初始缩放比例 */
const BUTTON_INITIAL_SCALE = 0.8
/** 开始按钮弹性效果中间缩放比例 */
const BUTTON_ELASTIC_SCALE = 1.05
/** 开始按钮最终缩放比例 */
const BUTTON_FINAL_SCALE = 1

// ==================== 类型定义 ====================
export interface PreloaderProps {
    /** 是否激活预加载器 */
    active?: boolean
    /** 预加载任务数组 */
    tasks: Array<() => Promise<void>>
    /** 是否已完成加载 */
    finished?: boolean
    /** 加载完成回调 */
    onFinish?: () => void
    /** 点击开始按钮回调 */
    onStart?: () => void
}

// ==================== 组件主体 ====================
/**
 * Preloader 预加载组件
 *
 * 功能：
 * 1. 执行预加载任务（图片等资源）
 * 2. 显示进度条动画（根据任务完成数量实时更新）
 * 3. 加载完成后执行完成动画序列：
 *    - icon旋转 → 进度条渐隐 → 开始按钮弹入
 */
export const Preloader: FC<PreloaderProps> = ({
    tasks,
    finished,
    onFinish,
    onStart,
}) => {
    // ==================== Refs 定义 ====================
    /** 进度条icon元素引用 */
    const endpointRef = useRef<HTMLImageElement>(null)
    /** 进度条当前进度容器引用（控制进度条宽度） */
    const currentWrapperRef = useRef<HTMLDivElement>(null)
    /** 整个进度条容器引用 */
    const progressRef = useRef<HTMLDivElement>(null)
    /** 开始按钮容器引用 */
    const startBtnRef = useRef<HTMLDivElement>(null)
    /** 进度百分比文字状态 */
    const [progressText, setProgressText] = useState("0%")
    /** 已完成的任务数量 */
    const [completedTasks, setCompletedTasks] = useState(0)
    /** 标记完成动画是否已启动（防止重复执行） */
    const completionAnimationStartedRef = useRef(false)
    /** 使用ref存储onFinish回调，避免useEffect依赖变化导致重复执行 */
    const onFinishRef = useRef(onFinish)
    /** 标记任务是否已开始执行（防止重复执行） */
    const tasksStartedRef = useRef(false)

    // ==================== Effects ====================
    /**
     * 同步更新onFinishRef
     * 使用ref存储回调函数，避免在useEffect依赖项中直接使用onFinish
     */
    useEffect(() => {
        onFinishRef.current = onFinish
    }, [onFinish])

    /**
     * 初始化进度条状态
     * 确保进度条从0%开始
     */
    useEffect(() => {
        // 初始化进度条为0%
        if (currentWrapperRef.current) {
            currentWrapperRef.current.style.width = "0%"
        }
        if (endpointRef.current) {
            endpointRef.current.style.left = "0%"
            endpointRef.current.style.transform = "translate(-0%, -50%)"
        }
        setProgressText("0%")
    }, [])

    /**
     * 进度条进入动画
     * 立即执行，从下方滑入 + 淡入
     * 动画效果：从下方滑入 + 淡入
     */
    useEffect(() => {
        if (progressRef.current) {
            const initialY = scalePx(PROGRESS_ENTRY_INITIAL_Y_DESIGN)
            progressRef.current.style.opacity = "0"
            progressRef.current.style.transform = `translateX(-44%) translateY(${initialY}px)`
            // 立即显示进度条
            const progressControl = {
                opacity: 0,
                translateY: initialY,
            }
            animate(progressControl, {
                opacity: [0, 1],
                translateY: [initialY, 0],
                duration: PROGRESS_ENTRY_DURATION,
                ease: eases.outCubic,
                onUpdate: () => {
                    if (progressRef.current) {
                        progressRef.current.style.opacity = `${progressControl.opacity}`
                        progressRef.current.style.transform = `translateX(-44%) translateY(${progressControl.translateY}px)`
                    }
                },
            })
        }
    }, [])

    /**
     * 执行预加载任务并跟踪进度
     * 在后台执行所有预加载任务（图片等资源），并实时更新进度条
     */
    useEffect(() => {
        // 防止重复执行
        if (tasksStartedRef.current || tasks.length === 0) return
        tasksStartedRef.current = true

        // 执行所有任务，并跟踪完成数量
        const totalTasks = tasks.length

        const executeTasks = async () => {
            // 使用Promise.allSettled来并行执行所有任务，并跟踪每个任务的完成状态
            const taskPromises = tasks.map(async (task, index) => {
                try {
                    await task()
                    // 使用函数式更新，确保状态更新的准确性
                    setCompletedTasks((prev) => {
                        const newCount = prev + 1
                        // 如果所有任务都完成了，调用onFinish
                        if (newCount === totalTasks) {
                            // 使用setTimeout确保状态更新后再调用回调
                            setTimeout(() => {
                                onFinishRef.current?.()
                            }, 0)
                        }
                        return newCount
                    })
                } catch (error) {
                    // 即使任务失败，也计入完成数量，避免进度卡住
                    setCompletedTasks((prev) => {
                        const newCount = prev + 1
                        // 如果所有任务都完成了，调用onFinish
                        if (newCount === totalTasks) {
                            setTimeout(() => {
                                onFinishRef.current?.()
                            }, 0)
                        }
                        return newCount
                    })
                    console.error(`任务 ${index} 执行失败:`, error)
                }
            })

            // 等待所有任务完成（即使有失败的任务也会继续）
            await Promise.allSettled(taskPromises)
        }

        executeTasks()
    }, [tasks])

    /**
     * 根据任务完成数量更新进度条
     * 实时反映真实加载进度
     */
    useEffect(() => {
        if (tasks.length === 0) return

        // 计算当前进度百分比
        const percentage = Math.min((completedTasks / tasks.length) * 100, 100)
        const roundedPercentage = Math.round(percentage)

        // 更新进度条UI：进度条宽度、icon位置、百分比文字
        if (currentWrapperRef.current) {
            currentWrapperRef.current.style.width = `${percentage.toFixed(2)}%`
        }
        if (endpointRef.current) {
            endpointRef.current.style.left = `${percentage.toFixed(2)}%`
            endpointRef.current.style.transform = `translate(-${percentage.toFixed(2)}%,-50%)`
        }
        setProgressText(`${roundedPercentage}%`)
    }, [completedTasks, tasks.length])

    /**
     * 完成动画序列
     * 当finished变为true时触发，执行以下动画序列：
     * 1. icon旋转一圈（ICON_ROTATION_DURATION）
     * 2. 进度条渐隐 + 上移 + 缩放（PROGRESS_EXIT_DURATION）
     * 3. 开始按钮弹入显示（BUTTON_ENTRY_DURATION）
     */
    useEffect(() => {
        if (finished && !completionAnimationStartedRef.current) {
            completionAnimationStartedRef.current = true

            // 确保进度条和icon都在100%位置
            if (currentWrapperRef.current) {
                currentWrapperRef.current.style.width = "100%"
            }
            if (endpointRef.current) {
                endpointRef.current.style.left = "100%"
                endpointRef.current.style.transform = "translate(-100%, -50%)"
            }
            setProgressText("100%")

            // 使用setTimeout确保DOM更新完成后再执行动画
            setTimeout(() => {
                // ========== 步骤1：进度icon旋转动画 ==========
                if (endpointRef.current) {
                    // 再次确保icon在100%位置
                    endpointRef.current.style.left = "100%"
                    const iconRotationControl = { rotation: 0 }
                    animate(iconRotationControl, {
                        rotation: [0, ICON_ROTATION_ANGLE],
                        duration: ICON_ROTATION_DURATION,
                        ease: eases.outCubic,
                        onUpdate: () => {
                            if (endpointRef.current) {
                                // 保持位置在100%，添加旋转效果
                                endpointRef.current.style.left = "100%"
                                endpointRef.current.style.transform = `translate(-100%, -50%) rotate(${iconRotationControl.rotation}deg)`
                            }
                        },
                        onComplete: () => {
                            // ========== 步骤2：进度条整体渐隐 ==========
                            if (progressRef.current) {
                                const exitTranslateY = scalePx(
                                    PROGRESS_EXIT_TRANSLATE_Y_DESIGN
                                )
                                const progressExitControl = {
                                    opacity: 1,
                                    translateY: 0,
                                    scale: 1,
                                }
                                animate(progressExitControl, {
                                    opacity: [1, 0],
                                    translateY: [0, exitTranslateY],
                                    scale: [1, PROGRESS_EXIT_SCALE],
                                    duration: PROGRESS_EXIT_DURATION,
                                    ease: eases.outCubic,
                                    onUpdate: () => {
                                        if (progressRef.current) {
                                            progressRef.current.style.opacity = `${progressExitControl.opacity}`
                                            progressRef.current.style.transform = `translateX(-44%) translateY(${progressExitControl.translateY}px) scale(${progressExitControl.scale})`
                                        }
                                    },
                                    onComplete: () => {
                                        // ========== 步骤3：开始按钮弹入显示 ==========
                                        if (progressRef.current) {
                                            progressRef.current.style.pointerEvents =
                                                "none"
                                        }
                                        if (startBtnRef.current) {
                                            startBtnRef.current.style.opacity =
                                                "0"
                                            startBtnRef.current.style.transform = `translateX(-44%) scale(${BUTTON_INITIAL_SCALE})`
                                            const btnControl = {
                                                opacity: 0,
                                                scale: BUTTON_INITIAL_SCALE,
                                            }
                                            animate(btnControl, {
                                                opacity: [0, 1],
                                                scale: [
                                                    BUTTON_INITIAL_SCALE,
                                                    BUTTON_ELASTIC_SCALE,
                                                    BUTTON_FINAL_SCALE,
                                                ],
                                                duration: BUTTON_ENTRY_DURATION,
                                                ease: eases.outCubic,
                                                onUpdate: () => {
                                                    if (startBtnRef.current) {
                                                        startBtnRef.current.style.opacity = `${btnControl.opacity}`
                                                        startBtnRef.current.style.transform = `translateX(-44%) scale(${btnControl.scale})`
                                                    }
                                                },
                                                onComplete: () => {
                                                    // 动画完成后，允许点击按钮
                                                    if (startBtnRef.current) {
                                                        startBtnRef.current.style.pointerEvents =
                                                            "auto"
                                                    }
                                                },
                                            })
                                        }
                                    },
                                })
                            }
                        },
                    })
                } else {
                    // 如果endpointRef不存在，直接执行后续动画
                    // ========== 步骤2：进度条整体渐隐 ==========
                    if (progressRef.current) {
                        const exitTranslateY = scalePx(
                            PROGRESS_EXIT_TRANSLATE_Y_DESIGN
                        )
                        const progressExitControl = {
                            opacity: 1,
                            translateY: 0,
                            scale: 1,
                        }
                        animate(progressExitControl, {
                            opacity: [1, 0],
                            translateY: [0, exitTranslateY],
                            scale: [1, PROGRESS_EXIT_SCALE],
                            duration: PROGRESS_EXIT_DURATION,
                            ease: eases.inQuad,
                            onUpdate: () => {
                                if (progressRef.current) {
                                    progressRef.current.style.opacity = `${progressExitControl.opacity}`
                                    progressRef.current.style.transform = `translateX(-44%) translateY(${progressExitControl.translateY}px) scale(${progressExitControl.scale})`
                                }
                            },
                            onComplete: () => {
                                // ========== 步骤3：开始按钮弹入显示 ==========
                                if (progressRef.current) {
                                    progressRef.current.style.pointerEvents =
                                        "none"
                                }
                                if (startBtnRef.current) {
                                    startBtnRef.current.style.opacity = "0"
                                    startBtnRef.current.style.transform = `translateX(-44%) scale(${BUTTON_INITIAL_SCALE})`
                                    const btnControl = {
                                        opacity: 0,
                                        scale: BUTTON_INITIAL_SCALE,
                                    }
                                    animate(btnControl, {
                                        opacity: [0, 1],
                                        scale: [
                                            BUTTON_INITIAL_SCALE,
                                            BUTTON_ELASTIC_SCALE,
                                            BUTTON_FINAL_SCALE,
                                        ],
                                        duration: BUTTON_ENTRY_DURATION,
                                        ease: eases.outElastic(1, 0.5),
                                        onUpdate: () => {
                                            if (startBtnRef.current) {
                                                startBtnRef.current.style.opacity = `${btnControl.opacity}`
                                                startBtnRef.current.style.transform = `translateX(-44%) scale(${btnControl.scale})`
                                            }
                                        },
                                        onComplete: () => {
                                            // 动画完成后，允许点击按钮
                                            if (startBtnRef.current) {
                                                startBtnRef.current.style.pointerEvents =
                                                    "auto"
                                            }
                                        },
                                    })
                                }
                            },
                        })
                    }
                }
            }, 50) // 给一个小的延迟确保DOM更新完成
        }
    }, [finished])

    return (
        <>
            {/*
                SVG clipPath 定义手绘风格的凹凸边缘
                用于创建进度条的不规则边缘效果
            */}
            <svg width="0" height="0" style={{ position: "absolute" }}>
                <defs>
                    {/*
                        inner 的凹凸边缘
                        - SVG只处理凹凸边缘，圆角由CSS border-radius控制
                        - 凹凸幅度控制在2px内 (2px/30px ≈ 0.067)
                        - 只向外凸起，不向内凹陷
                    */}
                    <clipPath
                        id="inner-rough-edge"
                        clipPathUnits="objectBoundingBox"
                    >
                        <path
                            d="M 0,0
                               L 0.008,0.017 L 0.019,0.055 L 0.031,0.028 L 0.044,0.068 L 0.058,0.038 L 0.073,0.062 L 0.089,0.025 L 0.106,0.075 L 0.124,0.045 L 0.143,0.065 L 0.163,0.035 L 0.184,0.078 L 0.206,0.05 L 0.229,0.058 L 0.253,0.03 L 0.278,0.072 L 0.304,0.055 L 0.331,0.068 L 0.359,0.042 L 0.388,0.082 L 0.418,0.058 L 0.449,0.068 L 0.481,0.048 L 0.514,0.078 L 0.548,0.062 L 0.583,0.068 L 0.619,0.055 L 0.656,0.078 L 0.694,0.065 L 0.733,0.05 L 0.773,0.072 L 0.814,0.058 L 0.856,0.068 L 0.899,0.048 L 0.943,0.075 L 0.988,0.062 L 1,0
                               L 1,1
                               L 0.988,0.938 L 0.943,0.945 L 0.899,0.932 L 0.856,0.952 L 0.814,0.925 L 0.773,0.945 L 0.733,0.935 L 0.694,0.912 L 0.656,0.948 L 0.619,0.918 L 0.583,0.928 L 0.548,0.912 L 0.514,0.942 L 0.481,0.928 L 0.449,0.932 L 0.418,0.912 L 0.388,0.962 L 0.359,0.942 L 0.331,0.932 L 0.304,0.912 L 0.278,0.958 L 0.253,0.945 L 0.229,0.935 L 0.206,0.922 L 0.184,0.962 L 0.163,0.935 L 0.143,0.925 L 0.124,0.918 L 0.106,0.945 L 0.089,0.918 L 0.073,0.918 L 0.058,0.948 L 0.044,0.928 L 0.031,0.932 L 0.019,0.945 L 0.008,0.938 L 0,1
                               Z"
                        />
                    </clipPath>
                    {/*
                        current 的凹凸边缘
                        - SVG只处理凹凸边缘，圆角由CSS border-radius控制
                        - 凹凸幅度控制在2px内
                        - 只向外凸起，不向内凹陷
                    */}
                    <clipPath
                        id="current-rough-edge"
                        clipPathUnits="objectBoundingBox"
                    >
                        <path
                            d="M 0,0
                               L 0.01,0.022 L 0.022,0.055 L 0.035,0.032 L 0.049,0.068 L 0.064,0.042 L 0.08,0.062 L 0.097,0.037 L 0.115,0.072 L 0.134,0.048 L 0.154,0.065 L 0.175,0.04 L 0.197,0.075 L 0.22,0.055 L 0.244,0.068 L 0.269,0.045 L 0.295,0.078 L 0.322,0.058 L 0.35,0.068 L 0.379,0.052 L 0.409,0.082 L 0.44,0.062 L 0.472,0.072 L 0.505,0.055 L 0.539,0.082 L 0.574,0.068 L 0.61,0.062 L 0.647,0.078 L 0.685,0.065 L 0.724,0.05 L 0.764,0.075 L 0.805,0.058 L 0.847,0.068 L 0.89,0.048 L 0.934,0.072 L 0.979,0.055 L 1,0
                               L 1,1
                               L 0.979,0.945 L 0.934,0.928 L 0.89,0.952 L 0.847,0.925 L 0.805,0.942 L 0.764,0.935 L 0.724,0.95 L 0.685,0.922 L 0.647,0.958 L 0.61,0.932 L 0.574,0.938 L 0.539,0.918 L 0.505,0.945 L 0.472,0.928 L 0.44,0.932 L 0.409,0.918 L 0.379,0.948 L 0.35,0.932 L 0.322,0.942 L 0.295,0.922 L 0.269,0.955 L 0.244,0.932 L 0.22,0.945 L 0.197,0.925 L 0.175,0.96 L 0.154,0.935 L 0.134,0.952 L 0.115,0.928 L 0.097,0.963 L 0.08,0.938 L 0.064,0.958 L 0.049,0.932 L 0.035,0.968 L 0.022,0.945 L 0.01,0.962 L 0,1
                               Z"
                        />
                    </clipPath>
                </defs>
            </svg>

            {/* 进度条区域 - 动画控制显示/隐藏 */}
            <div ref={progressRef} className={styles.progress}>
                <div className={styles.nowLoading} />
                <div className={styles.line}>
                    <div className={styles.progressBar}>
                        <div className={styles.innerWrapper}>
                            <div className={styles.inner}>
                                <div
                                    ref={currentWrapperRef}
                                    className={styles.currentWrapper}
                                >
                                    <div className={styles.current} />
                                </div>
                            </div>
                        </div>
                        <div
                            ref={endpointRef}
                            className={styles.progressIcon}
                        />
                    </div>
                    <div className={styles.progressText}>
                        <OutlinedText
                            className={styles.innerText}
                            text={progressText}
                            fontSize={50}
                            strokeColor="#EAE0CE"
                            strokeWidth={2}
                            fillColor="#c4422d"
                            outerStrokeColor="#906F45"
                            outerStrokeWidth={6}
                            fontFamily="Giants-Bold"
                        />
                    </div>
                </div>
            </div>

            {/* 开始按钮区域 - 动画控制显示/隐藏 */}
            <div
                ref={startBtnRef}
                className={styles.startBtnWrapper}
                style={{
                    opacity: 0,
                    pointerEvents: "none",
                }}
            >
                <OutlineButton
                    className={classNames(styles.startBtn)}
                    onClick={onStart}
                    text={i18n.get("loading.button")}
                    fontSize={64}
                    strokeWidth={6}
                    fillGradient={{ from: "#FFFBF7", to: "#FDF2D3" }}
                    strokeColor="#8A2E19"
                />
            </div>
        </>
    )
}
